import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  makeStyles,
  CircularProgress,
  Box,
  Divider,
  Typography,
} from '@material-ui/core';
import { SupportService } from '../../services/SupportService';
import { useDialogStyles } from './Users/shared/dialogStyles';
import "../../App.css";


const useStyles = makeStyles(theme => ({
  formControl: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  textarea: {
    marginTop: theme.spacing(8),
  },
}));

/**
 * SupportRequestDialog
 * 
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - institutionID: string | number
 *  - evaluators: Array<{ id: string|number, name: string, email: string }>
 */
export default function SupportRequestDialog({
  open,
  onClose,
  reason_param='',
  evaluationID=null
}) {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();

  const [reason, setReason] = useState(reason_param);
  const defaultDescription = evaluationID
  ? `Hello Support Team,\n\nI need assistance interpreting my report for Evaluation: ${evaluationID}.\n\nThank you!`
  : '';
  const [description, setDescription] = useState(defaultDescription);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const portal_user = sessionStorage.getItem('username');

  // Reset fields every time the dialog is opened
  useEffect(() => {
    if (open) {
      // Reset reason and description to their initial values
      setReason(reason_param);
      setDescription(defaultDescription);
      setError(null);
    }
    // eslint-disable-next-line
  }, [open, reason_param, defaultDescription]);

const helperText =
evaluationID
  ? "Please write here any information that could help the ViewMind team with the report interpretation. Don't include any personal identifiable information."
  : '';

  const handleSend = async () => {
    setLoading(true);
    setError(null);

    try {
      await SupportService.sendSupportRequest(reason, description);
      onClose();
    }finally {
      setLoading(false);
    }
  };


  const isSendDisabled = !reason || !description || loading;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm" 
      PaperProps={{ style: { borderRadius: 16, padding: '8px' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color='primary'>
            Support Request
          </Typography>
          <Button onClick={onClose} style={{margin: "0px", fontSize: "16px"}}>
            &times;
          </Button>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <FormControl  className={classes.formControl}>
          <InputLabel id="reason-label">Reason for support request</InputLabel>
          <Select
            labelId="reason-label"
            value={reason}
            onChange={e => setReason(e.target.value)}
            label="Reason for support request"
          >
            <MenuItem value="Report Generation">Report Generation</MenuItem>
            <MenuItem value="Report Interpretation">Report Interpretation</MenuItem>
            <MenuItem value="User Management">User Management</MenuItem>
            <MenuItem value="Demo Evaluations">Demo Evaluations</MenuItem>
            <MenuItem value="Evaluator Training">Evaluator Training</MenuItem>
            <MenuItem value="Evaluator Exam">Evaluator Exam</MenuItem>
            <MenuItem value="Technical Issue">Technical Issue</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl  className={classes.formControl}>
          <InputLabel id="user-label">User</InputLabel>
          <Select
            labelId="user-label"
            value={portal_user}
            disabled={true}
            label="Evaluator"
          >
              <MenuItem key={portal_user} value={portal_user}>
                {portal_user}
              </MenuItem>
          </Select>
        </FormControl>

        <TextField
          className={classes.textarea}
          label="Please describe the issue"
          variant="outlined"
          multiline
          rows={6}
          fullWidth
          value={description}
          onChange={e => setDescription(e.target.value)}
          helperText={helperText}
        />
        {error && (
          <Box color="error.main" mt={1}>
            {error}
          </Box>
        )}
      </DialogContent>

      <DialogActions className={dialogClasses.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          className={dialogClasses.button}
        >
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSend}
          disabled={isSendDisabled}
          className={dialogClasses.button}
        >
          {loading ? <CircularProgress size={24} /> : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SupportRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  institutionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  evaluators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    })
  ),
};
